import { useEffect, useMemo, useRef, useState } from "react";
import { Globe2, MapPin, Maximize2, Plane, RotateCcw, Trophy } from "lucide-react";
import type { ExportMarket } from "../../data/exportIntelligence";

const LEAFLET_SCRIPT_ID = "csjw-leaflet-script";
const LEAFLET_STYLE_ID = "csjw-leaflet-style";
const LEAFLET_VERSION = "1.9.4";
const INDONESIA: [number, number] = [-2.5489, 118.0149];

type LeafletMap = {
  remove: () => void;
  invalidateSize: () => void;
  fitBounds: (bounds: unknown, options?: Record<string, unknown>) => void;
  flyTo: (latLng: [number, number], zoom: number, options?: Record<string, unknown>) => void;
};

type LeafletLayer = {
  addTo: (map: LeafletMap | LeafletLayer) => LeafletLayer;
  clearLayers?: () => void;
  bindTooltip?: (content: string, options?: Record<string, unknown>) => LeafletLayer;
  on?: (event: string, handler: () => void) => LeafletLayer;
  bringToFront?: () => LeafletLayer;
};

type LeafletApi = {
  map: (element: HTMLElement, options?: Record<string, unknown>) => LeafletMap;
  tileLayer: (url: string, options?: Record<string, unknown>) => LeafletLayer;
  layerGroup: () => LeafletLayer;
  circleMarker: (latLng: [number, number], options?: Record<string, unknown>) => LeafletLayer;
  polyline: (latLngs: [number, number][], options?: Record<string, unknown>) => LeafletLayer;
  latLngBounds: (latLngs: [number, number][]) => unknown;
  control: {
    scale: (options?: Record<string, unknown>) => { addTo: (map: LeafletMap) => unknown };
  };
};

declare global {
  interface Window {
    L?: LeafletApi;
    __csjwLeafletPromise?: Promise<LeafletApi>;
  }
}

type WorldOpportunityMapProps = {
  markets: ExportMarket[];
  selectedId: string;
  onSelect: (market: ExportMarket) => void;
};

const scorePalette = (score: number) => {
  if (score >= 85) return { fill: "#10b981", stroke: "#d1fae5", glow: "rgba(16,185,129,.35)" };
  if (score >= 78) return { fill: "#f59e0b", stroke: "#fef3c7", glow: "rgba(245,158,11,.32)" };
  return { fill: "#38bdf8", stroke: "#e0f2fe", glow: "rgba(56,189,248,.28)" };
};

const escapeHtml = (value: string) => value
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&#039;");

const markerRadius = (score: number, minScore: number, maxScore: number) => {
  if (maxScore === minScore) return 17;
  const normalized = (score - minScore) / (maxScore - minScore);
  return Math.round(10 + normalized * 14);
};

function loadLeaflet(): Promise<LeafletApi> {
  if (window.L) return Promise.resolve(window.L);
  if (window.__csjwLeafletPromise) return window.__csjwLeafletPromise;

  window.__csjwLeafletPromise = new Promise<LeafletApi>((resolve, reject) => {
    if (!document.getElementById(LEAFLET_STYLE_ID)) {
      const style = document.createElement("link");
      style.id = LEAFLET_STYLE_ID;
      style.rel = "stylesheet";
      style.href = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;
      style.integrity = "sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=";
      style.crossOrigin = "";
      document.head.appendChild(style);
    }

    const existing = document.getElementById(LEAFLET_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => window.L ? resolve(window.L) : reject(new Error("Leaflet gagal dimuat.")), { once: true });
      existing.addEventListener("error", () => reject(new Error("Leaflet gagal dimuat.")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = LEAFLET_SCRIPT_ID;
    script.src = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;
    script.integrity = "sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=";
    script.crossOrigin = "";
    script.async = true;
    script.onload = () => window.L ? resolve(window.L) : reject(new Error("Leaflet tidak tersedia setelah dimuat."));
    script.onerror = () => reject(new Error("Leaflet gagal dimuat. Periksa koneksi internet."));
    document.body.appendChild(script);
  });

  return window.__csjwLeafletPromise;
}

export function WorldOpportunityMap({ markets, selectedId, onSelect }: WorldOpportunityMapProps) {
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const vectorLayerRef = useRef<LeafletLayer | null>(null);
  const onSelectRef = useRef(onSelect);
  const [mapStatus, setMapStatus] = useState<"loading" | "ready" | "error">("loading");

  const rankedMarkets = useMemo(
    () => [...markets].sort((a, b) => b.score - a.score),
    [markets],
  );
  const selected = markets.find((item) => item.id === selectedId) || rankedMarkets[0];
  const highest = rankedMarkets[0];
  const minScore = Math.min(...markets.map((market) => market.score));
  const maxScore = Math.max(...markets.map((market) => market.score));

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  const fitAllMarkets = () => {
    const L = window.L;
    const map = mapRef.current;
    if (!L || !map) return;
    const bounds = L.latLngBounds([
      INDONESIA,
      ...markets.map((market) => [market.latitude, market.longitude] as [number, number]),
    ]);
    map.fitBounds(bounds, { padding: [42, 42], maxZoom: 3.4, animate: true });
  };

  useEffect(() => {
    let cancelled = false;
    let resizeObserver: ResizeObserver | null = null;

    loadLeaflet()
      .then((L) => {
        if (cancelled || !mapElementRef.current || mapRef.current) return;

        const map = L.map(mapElementRef.current, {
          zoomControl: true,
          scrollWheelZoom: true,
          minZoom: 1,
          maxZoom: 7,
          worldCopyJump: true,
          zoomSnap: 0.25,
        });

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors',
          maxZoom: 19,
          className: "csjw-market-map-tiles",
        }).addTo(map);

        L.control.scale({ imperial: false, position: "bottomright" }).addTo(map);
        mapRef.current = map;
        vectorLayerRef.current = L.layerGroup().addTo(map);

        const bounds = L.latLngBounds([
          INDONESIA,
          ...markets.map((market) => [market.latitude, market.longitude] as [number, number]),
        ]);
        map.fitBounds(bounds, { padding: [42, 42], maxZoom: 3.4 });
        setMapStatus("ready");

        resizeObserver = new ResizeObserver(() => map.invalidateSize());
        resizeObserver.observe(mapElementRef.current);
        window.setTimeout(() => map.invalidateSize(), 120);
      })
      .catch(() => {
        if (!cancelled) setMapStatus("error");
      });

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      mapRef.current?.remove();
      mapRef.current = null;
      vectorLayerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const L = window.L;
    const map = mapRef.current;
    const vectorLayer = vectorLayerRef.current;
    if (!L || !map || !vectorLayer || !vectorLayer.clearLayers || mapStatus !== "ready") return;

    vectorLayer.clearLayers();

    L.polyline(
      [INDONESIA, [selected.latitude, selected.longitude]],
      {
        color: "#fb923c",
        weight: 3,
        opacity: 0.88,
        dashArray: "8 10",
        className: "csjw-market-route",
      },
    ).addTo(vectorLayer);

    const originMarker = L.circleMarker(INDONESIA, {
      radius: 8,
      color: "#ffffff",
      weight: 3,
      fillColor: "#fb923c",
      fillOpacity: 1,
      className: "csjw-market-origin",
    });
    originMarker.bindTooltip?.("Indonesia · titik asal", {
      permanent: false,
      direction: "top",
      className: "csjw-market-tooltip",
    });
    originMarker.addTo(vectorLayer);

    rankedMarkets.forEach((market, index) => {
      const active = market.id === selected.id;
      const isHighest = market.id === highest.id;
      const radius = markerRadius(market.score, minScore, maxScore);
      const palette = scorePalette(market.score);

      if (active || isHighest) {
        L.circleMarker([market.latitude, market.longitude], {
          radius: radius + (active ? 10 : 7),
          color: active ? "#ffffff" : palette.stroke,
          weight: active ? 2 : 1.5,
          opacity: active ? 0.82 : 0.58,
          fillColor: palette.fill,
          fillOpacity: active ? 0.16 : 0.1,
          interactive: false,
          className: active ? "csjw-market-halo csjw-market-halo--active" : "csjw-market-halo",
        }).addTo(vectorLayer);
      }

      const marker = L.circleMarker([market.latitude, market.longitude], {
        radius: radius + (active ? 2 : 0),
        color: active ? "#ffffff" : palette.stroke,
        weight: active ? 4 : 3,
        opacity: 1,
        fillColor: palette.fill,
        fillOpacity: active ? 1 : 0.88,
        className: active ? "csjw-market-marker csjw-market-marker--active" : "csjw-market-marker",
      });

      marker.bindTooltip?.(
        `<div class="csjw-map-tooltip-card"><div><strong>#${index + 1} ${escapeHtml(market.country)}</strong><span>${escapeHtml(market.region)}</span></div><b>${market.score}/100</b></div>`,
        {
          direction: "top",
          offset: [0, -(radius + 4)],
          opacity: 1,
          className: "csjw-market-tooltip",
        },
      );

      marker.on?.("click", () => onSelectRef.current(market));
      marker.addTo(vectorLayer);
      if (active) marker.bringToFront?.();
    });

    map.flyTo([selected.latitude, selected.longitude], selected.region === "Amerika Utara" ? 2.5 : 3.15, {
      duration: 0.85,
      easeLinearity: 0.25,
    });
  }, [highest.id, mapStatus, markets, maxScore, minScore, rankedMarkets, selected]);

  return (
    <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#062b23] p-4 shadow-[0_30px_80px_rgba(6,43,35,.24)] sm:p-6">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_15%,rgba(16,185,129,.18),transparent_32%),radial-gradient(circle_at_85%_80%,rgba(249,115,22,.14),transparent_30%)]" />
      <div className="relative flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[.16em] text-emerald-200"><Globe2 size={15} /> Export Opportunity Radar</div>
          <h3 className="mt-2 text-xl font-black text-white">Peta pasar prioritas</h3>
          <p className="mt-2 max-w-2xl text-xs leading-5 text-white/55">Ukuran lingkaran mengikuti Opportunity Score. Semakin besar lingkarannya, semakin tinggi prioritas pasarnya untuk produk yang dipilih.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-300/20 bg-emerald-400/10 px-3 py-2 text-[10px] font-black uppercase tracking-[.12em] text-emerald-100"><Trophy size={13} /> Teratas: {highest.country} · {highest.score}</span>
          <button type="button" onClick={fitAllMarkets} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[.07] px-3 py-2 text-[10px] font-black uppercase tracking-[.12em] text-white/70 hover:bg-white/[.12] hover:text-white"><Maximize2 size={13} /> Lihat semua</button>
        </div>
      </div>

      <div className="relative mt-5 overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#08372d] shadow-inner">
        <div ref={mapElementRef} className="h-[430px] w-full sm:h-[520px]" aria-label="Peta interaktif prioritas pasar ekspor" />

        {mapStatus === "loading" && (
          <div className="absolute inset-0 grid place-items-center bg-[#08372d]">
            <div className="text-center text-white"><div className="mx-auto h-9 w-9 animate-spin rounded-full border-4 border-white/15 border-t-emerald-300" /><p className="mt-3 text-xs font-bold text-white/60">Memuat peta pasar…</p></div>
          </div>
        )}

        {mapStatus === "error" && (
          <div className="absolute inset-0 grid place-items-center bg-[#08372d] p-6">
            <div className="max-w-sm text-center"><Globe2 className="mx-auto text-emerald-300" size={34} /><h4 className="mt-3 font-black text-white">Peta belum dapat dimuat</h4><p className="mt-2 text-xs leading-5 text-white/55">Periksa koneksi internet karena data peta Leaflet dan OpenStreetMap dimuat secara daring.</p><button type="button" onClick={() => window.location.reload()} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-xs font-black text-emerald-900"><RotateCcw size={14} /> Muat ulang</button></div>
          </div>
        )}

        <div className="pointer-events-none absolute bottom-3 left-3 z-[500] flex items-center gap-2 rounded-xl border border-white/10 bg-slate-950/65 px-3 py-2 text-[10px] font-bold text-white/70 shadow-lg backdrop-blur">
          <Plane size={13} className="text-orange-300" /> Klik lingkaran untuk membuka analisis negara
        </div>
      </div>

      <div className="relative mt-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-white/10 bg-white/[.06] p-4">
          <p className="text-[10px] font-black uppercase tracking-[.14em] text-white/45">Pasar terpilih</p>
          <div className="mt-2 flex items-center gap-2 text-sm font-black text-white"><MapPin size={15} className="text-orange-300" /> {selected.country}</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[.06] p-4">
          <p className="text-[10px] font-black uppercase tracking-[.14em] text-white/45">Opportunity score</p>
          <p className="mt-2 text-2xl font-black text-emerald-300">{selected.score}<span className="text-sm text-white/30">/100</span></p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[.06] p-4">
          <p className="text-[10px] font-black uppercase tracking-[.14em] text-white/45">Momentum</p>
          <p className="mt-2 text-2xl font-black text-orange-300">+{selected.momentum}%</p>
        </div>
      </div>

      <div className="relative mt-4 overflow-x-auto pb-1 hide-scrollbar">
        <div className="flex min-w-max gap-2">
          {rankedMarkets.map((market, index) => {
            const active = market.id === selected.id;
            const palette = scorePalette(market.score);
            const diameter = markerRadius(market.score, minScore, maxScore);
            return (
              <button key={market.id} type="button" onClick={() => onSelect(market)} className={`group flex min-w-[148px] items-center gap-3 rounded-2xl border px-3 py-3 text-left transition ${active ? "border-emerald-300/45 bg-emerald-300/15" : "border-white/10 bg-white/[.05] hover:bg-white/[.09]"}`}>
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[10px] font-black text-white shadow-lg" style={{ width: Math.max(28, diameter * 1.45), height: Math.max(28, diameter * 1.45), background: palette.fill, boxShadow: `0 0 0 4px ${palette.glow}` }}>{index + 1}</span>
                <span><strong className="block text-xs font-black text-white">{market.iso2} · {market.country}</strong><span className="mt-1 block text-[10px] font-bold text-white/45">Score {market.score}</span></span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
